import { selectItemByNameAndType } from './projectSelectors';

// Build OptionMediaValue from either raw string or already-normalized value
export function buildOptionMediaValue(spec, rawVal) {
  const options = spec.options || [];
  if (!options.length) return null;

  // Already normalized (draft / UI)
  if (rawVal && typeof rawVal === 'object' && rawVal.source === 'option') {
    return rawVal;
  }

  // Backend value: string
  if (rawVal && typeof rawVal === 'string') {
    const name = extractOptionNameFromValue(rawVal, options);
    if (!name) return null;
    const opt = options.find((o) => o.name === name);
    if (!opt) return null;

    return {
      source: 'option',
      id: opt.name,
      name: opt.name,
      preview: opt.preview,
      url: opt.preview,
    };
  }

  // Fallback to template default
  const name = extractOptionNameFromValue(spec.value, options);
  if (!name) return null;
  const opt = options.find((o) => o.name === name);
  if (!opt) return null;

  return {
    source: 'option',
    id: opt.name,
    name: opt.name,
    preview: opt.preview,
    url: opt.preview,
  };
}

function extractOptionNameFromValue(val, options) {
  if (!val || typeof val !== 'string') return null;
  // If options are strings
  if (options.length > 0 && typeof options[0] === 'string') {
    if (options.includes(val)) return val;
    return null;
  }
  // If options are objects
  const opt = options.find((o) => o.name === val || String(o.value) === val);
  return opt ? opt.name : null;
}

// Build ProjectMediaValue from either:
// - our normalized object
// - job field object like { value, key, ... }
// - or nothing
export function buildProjectMediaValue(rawVal) {
  if (!rawVal) return null;

  // Already normalized
  if (typeof rawVal === 'object' && rawVal.source === 'project') {
    return rawVal;
  }

  // From job backend, assuming shape: { value, key, id? }
  if (rawVal && typeof rawVal === 'object' && ('value' in rawVal || 'set_value' in rawVal)) {
    const name = rawVal.value || rawVal.set_value;

    return {
      source: 'project',
      id: rawVal.id,
      name,
      key: rawVal.key,
      url: rawVal.url,
    };
  }

  // Template defaults for project media we can ignore for now
  return null;
}

// Get "raw value" from selectedJob.fields which can be of two shapes:
// - job from API: fields[cat][fname] = { value, type, ... }
// - draft-backed "job": fields[cat][fname] = FormValue directly
export function getJobRawValue(selectedJob, cat, fname) {
  const field = selectedJob?.fields?.[cat]?.[fname];
  if (!field) return undefined;

  // Drafts: we store FormValue directly
  if (typeof field === 'string' || (typeof field === 'object' && field?.source)) {
    return field;
  }

  // API jobs: if it's option-media or text/number, raw string is fine
  if (typeof field === 'object') {
    // ✅ keep whole object for project media (option=false) so we don't lose url/key/id
    if (field.type && ['image', 'audio', 'video'].includes(field.type) && field.option === false) {
      return field; // <-- important
    }

    if ('value' in field) return field.value;
    if ('set_value' in field) return field.set_value;
  }

  return field;
}
